/**
 * Serviço de integração entre cache do PWA e assetPreloader
 * 
 * Este serviço:
 * 1. Sincroniza o cache do Workbox com o assetPreloader
 * 2. Adiciona APIs para manipular o cache do service worker
 * 3. Facilita a integração entre o sistema existente e o PWA
 */

// Detectar se estamos em um ambiente que suporta service worker
const serviceWorkerSupported = 'serviceWorker' in navigator;

// Cache manual para casos em que o service worker não está disponível
let fallbackCache = new Map();

/**
 * Adiciona um asset ao cache do service worker
 * @param {string} assetId - ID do asset no sistema existente
 * @param {string} url - URL do asset a ser cacheado
 * @returns {Promise<boolean>} - Promise que resolve para true se o caching foi bem-sucedido
 */
export async function addToCache(assetId, url) {
  try {
    if (!url) {
      console.warn(`URL inválida para o asset ${assetId}`);
      return false;
    }
    
    // Se o service worker estiver disponível, use o cache do workbox
    if (serviceWorkerSupported && navigator.serviceWorker.controller) {
      // Comunicar com o service worker para adicionar o item ao cache
      navigator.serviceWorker.controller.postMessage({
        type: 'ADD_TO_CACHE',
        payload: {
          assetId,
          url
        }
      });
      
      return true;
    } else {
      // Fallback para cache local quando o service worker não está disponível
      fallbackCache.set(assetId, url);
      return true;
    }
  } catch (error) {
    console.error('Erro ao adicionar asset ao cache:', error);
    return false;
  }
}

/**
 * Recupera um asset do cache do service worker
 * @param {string} assetId - ID do asset no sistema existente
 * @returns {Promise<string|null>} - URL do asset ou null se não estiver no cache
 */
export async function getFromCache(assetId) {
  try {
    // Se não houver service worker, verificar no cache de fallback
    if (!serviceWorkerSupported || !navigator.serviceWorker.controller) {
      return fallbackCache.get(assetId) || null;
    }
    
    // Criar um canal de mensagem para a resposta
    return new Promise(resolve => {
      // ID único para este request
      const messageChannel = new MessageChannel();
      const requestId = Date.now().toString();
      
      // Canal para receber a resposta
      messageChannel.port1.onmessage = event => {
        if (event.data.requestId === requestId) {
          resolve(event.data.url || null);
        }
      };
      
      // Enviar a mensagem para o service worker
      navigator.serviceWorker.controller.postMessage({
        type: 'GET_FROM_CACHE',
        payload: {
          assetId,
          requestId
        }
      }, [messageChannel.port2]);
      
      // Timeout de 1 segundo caso não haja resposta
      setTimeout(() => resolve(null), 1000);
    });
  } catch (error) {
    console.error('Erro ao recuperar asset do cache:', error);
    return null;
  }
}

/**
 * Verifica se um asset está no cache do service worker
 * @param {string} assetId - ID do asset no sistema existente
 * @returns {Promise<boolean>} - Promise que resolve para true se o asset estiver no cache
 */
export async function isInCache(assetId) {
  try {
    // Se não houver service worker, verificar no cache de fallback
    if (!serviceWorkerSupported || !navigator.serviceWorker.controller) {
      return fallbackCache.has(assetId);
    }
    
    // Criar um canal de mensagem para a resposta
    return new Promise(resolve => {
      // ID único para este request
      const messageChannel = new MessageChannel();
      const requestId = Date.now().toString();
      
      // Canal para receber a resposta
      messageChannel.port1.onmessage = event => {
        if (event.data.requestId === requestId) {
          resolve(event.data.isInCache);
        }
      };
      
      // Enviar a mensagem para o service worker
      navigator.serviceWorker.controller.postMessage({
        type: 'CHECK_CACHE',
        payload: {
          assetId,
          requestId
        }
      }, [messageChannel.port2]);
      
      // Timeout de 1 segundo caso não haja resposta
      setTimeout(() => resolve(false), 1000);
    });
  } catch (error) {
    console.error('Erro ao verificar asset no cache:', error);
    return false;
  }
}

/**
 * Sincroniza assets do assetPreloader com o cache do service worker
 * @param {Array} assets - Lista de assets já carregados pelo assetPreloader
 * @returns {Promise<number>} - Número de assets sincronizados
 */
export async function syncWithServiceWorker(assets) {
  // Se não houver service worker, não faz nada
  if (!serviceWorkerSupported || !navigator.serviceWorker.controller) {
    return 0;
  }
  
  try {
    let syncedCount = 0;
    
    // Para cada asset já carregado, adicionar ao cache do service worker
    const syncPromises = Object.entries(assets).map(async ([assetId, url]) => {
      const added = await addToCache(assetId, url);
      if (added) syncedCount++;
    });
    
    await Promise.all(syncPromises);
    console.log(`Sincronizados ${syncedCount} assets com o service worker`);
    return syncedCount;
  } catch (error) {
    console.error('Erro ao sincronizar com service worker:', error);
    return 0;
  }
}

/**
 * Adiciona múltiplos assets ao cache simultaneamente
 * @param {Object} assetsMap - Mapa de assetId -> URL
 * @returns {Promise<number>} - Número de assets adicionados com sucesso
 */
export async function addMultipleToCache(assetsMap) {
  try {
    let successCount = 0;
    
    for (const [assetId, url] of Object.entries(assetsMap)) {
      const success = await addToCache(assetId, url);
      if (success) successCount++;
    }
    
    return successCount;
  } catch (error) {
    console.error('Erro ao adicionar múltiplos assets ao cache:', error);
    return 0;
  }
}

/**
 * Libera espaço no cache removendo assets de baixa prioridade
 * @param {number} requiredBytes - Bytes necessários para liberar
 * @param {Array} lowPriorityAssetIds - IDs de assets que podem ser removidos
 * @returns {Promise<boolean>} - Se foi possível liberar espaço
 */
export async function freeCacheSpace(requiredBytes, lowPriorityAssetIds) {
  // Se não houver service worker, não faz nada
  if (!serviceWorkerSupported || !navigator.serviceWorker.controller) {
    return false;
  }
  
  try {
    return new Promise(resolve => {
      // ID único para este request
      const messageChannel = new MessageChannel();
      const requestId = Date.now().toString();
      
      // Canal para receber a resposta
      messageChannel.port1.onmessage = event => {
        if (event.data.requestId === requestId) {
          resolve(event.data.success);
        }
      };
      
      // Enviar a mensagem para o service worker
      navigator.serviceWorker.controller.postMessage({
        type: 'FREE_CACHE_SPACE',
        payload: {
          requiredBytes,
          lowPriorityAssetIds,
          requestId
        }
      }, [messageChannel.port2]);
      
      // Timeout de 3 segundos caso não haja resposta
      setTimeout(() => resolve(false), 3000);
    });
  } catch (error) {
    console.error('Erro ao liberar espaço no cache:', error);
    return false;
  }
}

/**
 * Remove um asset específico do cache
 * @param {string} assetId - ID do asset a ser removido
 * @returns {Promise<boolean>} - Se a remoção foi bem-sucedida
 */
export async function removeFromCache(assetId) {
  // Se não houver service worker, remover do cache de fallback
  if (!serviceWorkerSupported || !navigator.serviceWorker.controller) {
    const hadAsset = fallbackCache.has(assetId);
    fallbackCache.delete(assetId);
    return hadAsset;
  }
  
  try {
    return new Promise(resolve => {
      // ID único para este request
      const messageChannel = new MessageChannel();
      const requestId = Date.now().toString();
      
      // Canal para receber a resposta
      messageChannel.port1.onmessage = event => {
        if (event.data.requestId === requestId) {
          resolve(event.data.success);
        }
      };
      
      // Enviar a mensagem para o service worker
      navigator.serviceWorker.controller.postMessage({
        type: 'REMOVE_FROM_CACHE',
        payload: {
          assetId,
          requestId
        }
      }, [messageChannel.port2]);
      
      // Timeout de 1 segundo caso não haja resposta
      setTimeout(() => resolve(false), 1000);
    });
  } catch (error) {
    console.error('Erro ao remover asset do cache:', error);
    return false;
  }
}

/**
 * Limpar todo o cache do service worker
 * @returns {Promise<boolean>} - Se a limpeza foi bem-sucedida
 */
export async function clearAllCache() {
  try {
    // Se não houver service worker, remover do cache de fallback
    if (!serviceWorkerSupported || !navigator.serviceWorker.controller) {
      fallbackCache.clear();
      return true;
    }
    
    return new Promise(resolve => {
      // ID único para este request
      const messageChannel = new MessageChannel();
      const requestId = Date.now().toString();
      
      // Canal para receber a resposta
      messageChannel.port1.onmessage = event => {
        if (event.data.requestId === requestId) {
          resolve(event.data.success);
        }
      };
      
      // Enviar a mensagem para o service worker
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_ALL_CACHE',
        payload: {
          requestId
        }
      }, [messageChannel.port2]);
      
      // Timeout de 3 segundos caso não haja resposta
      setTimeout(() => resolve(false), 3000);
    });
  } catch (error) {
    console.error('Erro ao limpar todo o cache:', error);
    return false;
  }
}

/**
 * Obter informações sobre o cache do service worker
 * @returns {Promise<Object>} - Informações sobre o cache
 */
export async function getCacheInfo() {
  try {
    // Se não houver service worker, retornar informações do fallback
    if (!serviceWorkerSupported || !navigator.serviceWorker.controller) {
      return {
        itemCount: fallbackCache.size,
        size: 0,
        lastUpdated: Date.now()
      };
    }
    
    return new Promise(resolve => {
      // ID único para este request
      const messageChannel = new MessageChannel();
      const requestId = Date.now().toString();
      
      // Canal para receber a resposta
      messageChannel.port1.onmessage = event => {
        if (event.data.requestId === requestId) {
          resolve(event.data.cacheInfo || {
            itemCount: 0,
            size: 0,
            lastUpdated: Date.now()
          });
        }
      };
      
      // Enviar a mensagem para o service worker
      navigator.serviceWorker.controller.postMessage({
        type: 'GET_CACHE_INFO',
        payload: {
          requestId
        }
      }, [messageChannel.port2]);
      
      // Timeout de 1 segundo caso não haja resposta
      setTimeout(() => resolve({
        itemCount: 0,
        size: 0,
        lastUpdated: Date.now(),
        error: 'timeout'
      }), 1000);
    });
  } catch (error) {
    console.error('Erro ao obter informações do cache:', error);
    return {
      itemCount: 0,
      size: 0,
      lastUpdated: Date.now(),
      error: error.message
    };
  }
}

export default {
  addToCache,
  getFromCache,
  isInCache,
  syncWithServiceWorker,
  addMultipleToCache,
  freeCacheSpace,
  removeFromCache,
  clearAllCache,
  getCacheInfo
}; 